
import React from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../../types';
import { slugify } from '../../utils/slugify';
import { useDeliveryLabel } from '../../hooks/useDeliveryLabel';

interface ProductCardProps {
    product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
    // Lógica simulada de precios y descuentos para visualización
    const basePrice = product.quantityPricing?.[0]?.price ? Number(product.quantityPricing[0].price) : product.price;
    const originalPrice = basePrice * 1.25; // Simulamos un precio anterior 25% más alto
    const discountPercentage = 20;

    // Extracción de datos clave
    const minQty = product.quantityPricing?.[0]?.quantity || 1;
    const priceInfo = product.quantityPricing?.[0] ?? null;
    const deliveryTime = priceInfo?.deliveryTime || '24 hrs';
    const isExpress = deliveryTime.toLowerCase().includes('hr') || deliveryTime.includes('1 día');

    // Etiqueta calculada con horarios de corte reales (igual que el Cotizador Express)
    const deliveryLabel = useDeliveryLabel(priceInfo);

    const categorySlug = slugify(product.category);
    const productSlug = product.seo?.slug || slugify(product.name);
    const productUrl = categorySlug && productSlug ? `/${categorySlug}/${productSlug}` : '/';

    return (
        <Link
            to={productUrl}
            className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] transition-all duration-500 flex flex-col h-full transform hover:-translate-y-1"
        >
            {/* Image Container */}
            <div className="relative aspect-[4/3] overflow-hidden bg-gray-50">
                <img
                    src={product.imageUrls?.[0] || 'https://picsum.photos/seed/placeholder/600/400'}
                    alt={product.name}
                    className="w-full h-full object-cover object-center transition-transform duration-700 ease-in-out group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={450}
                />
                
                {/* Overlay gradiente suave al hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                    {discountPercentage > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm animate-pulse-slow">
                            -{discountPercentage}% OFF
                        </span>
                    )}
                    {isExpress && (
                        <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm flex items-center gap-1">
                            <i className="fas fa-bolt text-[10px]"></i> Express
                        </span>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="p-6 flex flex-col flex-grow">
                {/* Category & Rating */}
                <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-50 px-2 py-0.5 rounded-md">
                        {product.category}
                    </span>
                    <div className="flex items-center text-yellow-400 text-xs gap-0.5">
                        <i className="fas fa-star"></i>
                        <i className="fas fa-star"></i>
                        <i className="fas fa-star"></i>
                        <i className="fas fa-star"></i>
                        <i className="fas fa-star-half-alt"></i>
                        <span className="text-gray-400 ml-1 font-medium">(4.8)</span>
                    </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2 leading-tight group-hover:text-blue-600 transition-colors">
                    {product.name}
                </h3>
                
                <p className="text-gray-500 text-sm line-clamp-2 mb-4 flex-grow">
                    {product.description}
                </p>

                {/* Meta Info Grid */}
                <div className="grid grid-cols-2 gap-3 mb-5 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="fas fa-box text-gray-400"></i>
                        <span>Min: <strong>{minQty} pzas</strong></span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="far fa-clock text-gray-400"></i>
                        <span><strong>{deliveryLabel}</strong></span>
                    </div>
                </div>

                {/* Price & CTA */}
                <div className="flex items-end justify-between gap-4 mt-auto">
                    <div>
                        <p className="text-xs text-gray-400 line-through font-medium mb-0.5">
                            ${originalPrice.toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                        </p>
                        <div className="flex items-baseline gap-1">
                            <p className="text-2xl font-extrabold text-gray-900">
                                ${basePrice.toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                            </p>
                            <span className="text-xs text-gray-500 font-medium">MXN</span>
                        </div>
                    </div>
                    
                    <button aria-label={`Cotizar ${product.name}`} className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-md group-hover:w-auto group-hover:px-4 group-hover:rounded-lg transition-all duration-300 overflow-hidden">
                        <i className="fas fa-arrow-right group-hover:hidden"></i>
                        <span className="hidden group-hover:inline text-sm font-bold whitespace-nowrap">
                            Cotizar <i className="fas fa-chevron-right ml-1 text-xs"></i>
                        </span>
                    </button>
                </div>
            </div>
        </Link>
    );
};

export default ProductCard;
